import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

import algolia from '../utilities/algolia';
import Store from '../utilities/store';
import { StateManager } from '../utilities/stateManager';
import { useStackZIndex, ModalStackProvider } from '../utilities/ModalStackContext';

const formatCustomerLine = (c) => {
  const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || '(no name)';
  const parts = [c.email, c.phone_number || c.phone, c.address1, c.state].filter(Boolean);
  return { name, sub: parts.join(' · ') };
};

export default function MergeCustomersDialog({ open, primaryId, primaryCustomer, onClose, onComplete }) {
  const myZ = useStackZIndex();
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [selected, setSelected] = React.useState([]);
  const [merging, setMerging] = React.useState(false);
  const [error, setError] = React.useState('');
  const debounceRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setSelected([]);
      setMerging(false);
      setError('');
    }
  }, [open]);

  const onQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    clearTimeout(debounceRef.current);
    if (!value) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { hits } = await algolia.client.initIndex('customers').search(value, { hitsPerPage: 15 });
        setResults(hits || []);
      } catch (e) {
        console.error('Algolia customer search failed', e);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
  };

  const isAlreadyPicked = (id) => selected.some(s => s.id === id);

  const addDuplicate = (hit) => {
    const id = hit.objectID || hit.id;
    if (!id) return;
    if (id === primaryId) {
      setError("You can't add the current customer as their own duplicate.");
      return;
    }
    if (isAlreadyPicked(id)) return;
    setError('');
    setSelected(prev => [...prev, { id, ...hit }]);
    setQuery('');
    setResults([]);
  };

  const removeDuplicate = (id) => {
    setSelected(prev => prev.filter(s => s.id !== id));
  };

  const handleMerge = async () => {
    if (selected.length === 0) return;
    const dupNames = selected.map(s => `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.id).join(', ');
    const confirm = window.confirm(
      `This will merge ${selected.length} duplicate customer${selected.length > 1 ? 's' : ''} (${dupNames}) into the current customer. ` +
      `References (deals, cars, orders, leads) will be re-pointed and the duplicates will be deleted. This cannot be undone. Continue?`
    );
    if (!confirm) return;

    setMerging(true);
    setError('');
    try {
      await Store.mergeCustomers(primaryId, selected.map(s => s.id));
      onComplete?.(primaryId);
    } catch (e) {
      console.error('Merge failed', e);
      setError(e?.message || 'Merge failed.');
      StateManager.setAlertAndOpen(`Merge failed: ${e?.message || 'unknown error'}`, 'error');
    } finally {
      setMerging(false);
    }
  };

  const primaryLine = primaryCustomer ? formatCustomerLine(primaryCustomer) : { name: '—', sub: '' };

  return (
    <ModalStackProvider value={myZ}>
    <Dialog open={open} onClose={merging ? undefined : onClose} maxWidth="sm" fullWidth sx={{ zIndex: myZ }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Merge Duplicate Customers
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={onClose} disabled={merging} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Keep (primary)
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'success.light', mb: 2, opacity: 0.95 }}>
          <Avatar sx={{ bgcolor: 'success.main' }}><PersonIcon /></Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{primaryLine.name}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>{primaryLine.sub}</Typography>
          </Box>
          <Chip label="Primary" color="success" size="small" />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Duplicates to merge in
        </Typography>

        {selected.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ mb: 2, fontStyle: 'italic' }}>
            None selected yet — find them below.
          </Typography>
        ) : (
          <List dense sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0 }}>
            {selected.map((s) => {
              const line = formatCustomerLine(s);
              return (
                <ListItem
                  key={s.id}
                  secondaryAction={
                    <IconButton edge="end" onClick={() => removeDuplicate(s.id)} disabled={merging} size="small">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}><PersonIcon fontSize="small" /></Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={line.name}
                    secondary={line.sub}
                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                  />
                </ListItem>
              );
            })}
          </List>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, email, phone…"
          value={query}
          onChange={onQueryChange}
          disabled={merging}
          autoFocus
          InputProps={{
            endAdornment: searching ? <CircularProgress size={18} /> : null,
          }}
        />

        {results.length > 0 && (
          <List
            dense
            sx={{
              mt: 1, maxHeight: 260, overflow: 'auto',
              border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0,
            }}
          >
            {results.map((hit) => {
              const id = hit.objectID || hit.id;
              const line = formatCustomerLine(hit);
              const isPrimary = id === primaryId;
              const isPicked = isAlreadyPicked(id);
              return (
                <ListItem
                  key={id}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => addDuplicate(hit)}
                      disabled={isPrimary || isPicked || merging}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  }
                  sx={{ opacity: (isPrimary || isPicked) ? 0.5 : 1 }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32 }}><PersonIcon fontSize="small" /></Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${line.name}${isPrimary ? ' (current)' : isPicked ? ' (selected)' : ''}`}
                    secondary={line.sub}
                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                  />
                </ListItem>
              );
            })}
          </List>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}

        <Alert severity="warning" sx={{ mt: 2 }}>
          Merging re-points all references (deals, cars, orders, leads) and deletes the duplicate customer records. This cannot be undone.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={merging}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleMerge}
          disabled={merging || selected.length === 0 || !primaryId}
          startIcon={merging ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {merging ? 'Merging…' : `Merge ${selected.length || ''}`.trim()}
        </Button>
      </DialogActions>
    </Dialog>
    </ModalStackProvider>
  );
}
